import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setFilterField } from '../../app/itemGridPreferencesSlice';
import axiosInstance from '../../config/axiosApi/axiosApi';
import APIs from '../../Constants/apis';
import Preloader from '../../components/CustomComponents/preLoad/preLoader';
import showErrorToast from '../../components/CustomComponents/toast/toast';
import useThemeColor from '../../hooks/useThemeColor';
import useDarkMode from '../../hooks/useDarkMode';
import DateRangePicker from '../../components/CustomComponents/DateRangePicker/DateRangePicker';
import moment from 'moment';
import Select from 'react-select';
import DropDownStyles, { DropDownStylesDark } from '../staff/dropdownStyles';
import '../order/ManageOrderFilters.css';

const toValidDate = (v) => {
  if (v == null) return null;
  const d = typeof v === 'string' ? new Date(v) : v;
  return d instanceof Date && !isNaN(d.getTime()) ? d : null;
};

const StaffWorkReport = () => {
  const dispatch = useDispatch();
  const CompID = useSelector((state) => state.auth.user?.userData?.compID);
  const { profileData } = useSelector((state) => state.auth);
  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  const pageKey = 'staffWorkReportList';
  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const parentCompId = profileData?.parentComID ?? profileData?.ParentComID ?? 0;
  const isMain = !parentCompId || parseInt(parentCompId, 10) <= 0;
  const defaultLocation = useMemo(() => ({ value: 'all', label: 'All Locations' }), []);
  const parentIdForLocation = isMain && CompID != null ? (typeof CompID === 'number' ? CompID : parseInt(CompID, 10)) : null;
  const companyName = profileData?.compName ?? profileData?.CompName ?? 'Parent Branch';
  const location = preferences.location || defaultLocation;

  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [reportData, setReportData] = useState({
    summary: {},
    staffReports: [],
  });
  const [allStaffList, setAllStaffList] = useState([]);
  const [startDate, setStartDate] = useState(moment().startOf('day').toDate());
  const [endDate, setEndDate] = useState(moment().endOf('day').toDate());
  const [selectedStaff, setSelectedStaff] = useState({ value: '', label: 'All Staff' });
  const [compCurrency, setCompCurrency] = useState('GBP');

  const currencySymbols = {
    GBP: '£',
    USD: '$',
    EUR: '€',
    PKR: '₨',
    Rs: '₨',
  };

  const getCurrencySymbol = (currencyCode) => currencySymbols[currencyCode] || currencyCode;

  const normalizeCurrencyCode = (currencyCode) => {
    if (currencyCode === 'Rs') return 'PKR';
    return currencyCode || 'GBP';
  };

  useEffect(() => {
    if (profileData?.compCurrency) {
      setCompCurrency(normalizeCurrencyCode(profileData.compCurrency));
    }
  }, [profileData]);

  const fetchLocations = useCallback(async () => {
    if (!isMain || !CompID) return;
    try {
      setLoadingLocations(true);
      const response = await axiosInstance.get(APIs.GET_COMPANY_LOCATIONS, {
        params: { CompID },
      });
      const locationData = response.data?.data ?? response.data ?? [];
      const arr = Array.isArray(locationData) ? locationData : [];
      const parentId = typeof CompID === 'number' ? CompID : parseInt(CompID, 10);
      const compName = profileData?.compName ?? profileData?.CompName ?? 'Parent Branch';
      const base = [{ value: parentId, label: compName }];
      const branchOpts = arr
        .filter((loc) => {
          const pid = loc.ParentComID ?? loc.parentComID;
          const id = loc.CompID ?? loc.compID;
          const pidNum = pid != null ? (typeof pid === 'number' ? pid : parseInt(pid, 10)) : null;
          const idNum = id != null ? (typeof id === 'number' ? id : parseInt(id, 10)) : null;
          if (idNum == null || isNaN(idNum)) return false;
          if (pidNum === parentId && idNum !== parentId) return true;
          return false;
        })
        .map((loc) => {
          const id = loc.CompID ?? loc.compID;
          const name = String(loc.CompName ?? loc.compName ?? '').trim() || `Branch ${id ?? ''}`;
          const val = typeof id === 'number' ? id : parseInt(id, 10);
          return { value: isNaN(val) ? null : val, label: name };
        })
        .filter((o) => o.value != null);
      setLocations([...base, ...branchOpts]);
    } catch (error) {
      console.error('Error fetching locations:', error);
      showErrorToast(error.response?.data?.message || 'Error loading locations');
    } finally {
      setLoadingLocations(false);
    }
  }, [CompID, isMain, profileData?.compName, profileData?.CompName]);

  const allLocationsOption = { value: 'all', label: 'All Locations' };
  const locationOptions = locations.length > 0
    ? [allLocationsOption, ...locations]
    : parentIdForLocation != null && !isNaN(parentIdForLocation)
      ? [allLocationsOption, { value: parentIdForLocation, label: companyName }]
      : [allLocationsOption];

  useEffect(() => {
    if (isMain && !preferences.location) {
      dispatch(setFilterField({ pageKey, field: 'location', value: defaultLocation }));
    }
  }, [isMain, preferences.location, dispatch, pageKey, defaultLocation]);

  useEffect(() => {
    if (isMain) fetchLocations();
  }, [isMain, fetchLocations]);

  useEffect(() => {
    const fetchStaffList = async () => {
      try {
        const response = await axiosInstance.get(APIs.GET_COMPANY_STAFF, {
          params: {
            CompID,
            SortBy: 'First_Name',
            SortOrder: 'ASC',
            Search: '',
            PageSize: 1000,
            PageNumber: 1,
          },
        });
        const staffData = response.data?.data || response.data || [];
        setAllStaffList(Array.isArray(staffData) ? staffData : []);
      } catch (err) {
        showErrorToast('Failed to fetch staff list');
        console.error('Error fetching staff list:', err);
        setAllStaffList([]);
      }
    };
    if (CompID) fetchStaffList();
  }, [CompID]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchStaffWorkReport();
    }, 300);
    return () => clearTimeout(handler);
  }, [startDate, endDate, selectedStaff, location]);

  const fetchStaffWorkReport = async () => {
    if (!CompID) return;

    setLoading(true);
    try {
      const params = {
        compID: CompID,
        startDate: startDate && moment(startDate).isValid() ? moment(startDate).format('YYYY-MM-DD HH:mm:ss') : '',
        endDate: endDate && moment(endDate).isValid() ? moment(endDate).format('YYYY-MM-DD HH:mm:ss') : '',
      };
      if (selectedStaff?.value) {
        params.userName = selectedStaff.value;
      }
      if (isMain && location?.value !== 'all' && location?.value != null) {
        params.locationFilter = typeof location.value === 'number' ? location.value : parseInt(location.value, 10);
      }

      const response = await axiosInstance.get(APIs.GET_STAFF_WORK_REPORT_SALES, { params });

      const data = response.data;
      if (data) {
        const summary = data.summary || {};
        const staffReports = data.staffReports || [];
        setReportData({ summary, staffReports });
      } else {
        setReportData({ summary: {}, staffReports: [] });
      }
    } catch (err) {
      showErrorToast('Failed to fetch staff work report');
      console.error('Error fetching staff work report:', err);
      setReportData({ summary: {}, staffReports: [] });
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (range) => {
    const [newStart, newEnd] = range || [null, null];
    if (!newStart && !newEnd) return;
    let effectiveStart = newStart && moment(newStart).isValid() ? newStart : null;
    let effectiveEnd = newEnd && moment(newEnd).isValid() ? newEnd : null;
    if (effectiveStart && effectiveEnd && effectiveStart > effectiveEnd) {
      [effectiveStart, effectiveEnd] = [effectiveEnd, effectiveStart];
    } else if (effectiveStart && !effectiveEnd) {
      effectiveEnd = new Date(effectiveStart.getTime());
      effectiveEnd.setHours(23, 59, 59, 999);
    }
    if (effectiveStart) setStartDate(effectiveStart);
    if (effectiveEnd) setEndDate(effectiveEnd);
  };

  const handleLocationChange = (option) => {
    dispatch(setFilterField({ pageKey, field: 'location', value: option || defaultLocation }));
  };

  const clearFilters = () => {
    setStartDate(moment().startOf('day').toDate());
    setEndDate(moment().endOf('day').toDate());
    setSelectedStaff({ value: '', label: 'All Staff' });
    if (isMain) dispatch(setFilterField({ pageKey, field: 'location', value: defaultLocation }));
  };

  const formatCurrency = (amount) => {
    try {
      const normalized = normalizeCurrencyCode(compCurrency);
      const locale = normalized === 'USD' ? 'en-US' : normalized === 'EUR' ? 'en-EU' : normalized === 'PKR' ? 'en-PK' : 'en-GB';
      return new Intl.NumberFormat(locale, { style: 'currency', currency: normalized }).format(amount || 0);
    } catch (e) {
      return `${getCurrencySymbol(compCurrency)}${(amount || 0).toFixed(2)}`;
    }
  };

  const staffOptions = useMemo(
    () => [
      { value: '', label: 'All Staff' },
      ...allStaffList.map((staff) => ({
        value: staff.userName || staff.UserName,
        label: `${(staff.firstName || staff.FirstName || '').trim()} ${(staff.lastName || staff.LastName || '').trim()}`.trim() || staff.email || staff.userName || 'Unknown',
      })),
    ],
    [allStaffList]
  );

  const getCategorySales = (categorySales) => {
    if (!categorySales || typeof categorySales !== 'object') return [];
    return Object.values(categorySales);
  };

  const summary = reportData.summary || {};
  const categorySales = getCategorySales(summary.categorySales);
  const staffReports = reportData.staffReports || [];

  return (
    <div>
      {loading && <Preloader />}

      <ol className="breadcrumb float-xl-end">
        <li className="breadcrumb-item"><a href="#/">Home</a></li>
        <li className="breadcrumb-item"><a href="#/report">Reports</a></li>
        <li className="breadcrumb-item active">Staff Work Report</li>
      </ol>
      <h1 className="page-header">Staff Work Report</h1>

      <div className="mb-4" style={{ position: 'relative', zIndex: 100 }}>
        <div className="card filter-card" style={{ position: 'relative', zIndex: 100 }}>
          <div className="card-body p-4">
            <div className="row g-3 align-items-end">
              {isMain && locationOptions.length > 1 && (
                <div className="col-xl-4 col-lg-4 col-md-6">
                  <label className="form-label">
                    <i className="fa fa-map-marker-alt me-1"></i>Location
                  </label>
                  <Select
                    value={location}
                    onChange={handleLocationChange}
                    options={locationOptions}
                    styles={dropdownStyles}
                    isSearchable={false}
                    isLoading={loadingLocations}
                    placeholder="All Locations"
                    className="filter-dropdown"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>
              )}
              <div className="col-xl-4 col-lg-4 col-md-6">
                <label className="form-label">
                  <i className="fa fa-user me-1"></i>Select Staff
                </label>
                <Select
                  value={selectedStaff}
                  onChange={setSelectedStaff}
                  options={staffOptions}
                  styles={dropdownStyles}
                  placeholder="All Staff"
                  className="filter-dropdown"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
              <div className="col-xl-4 col-lg-4 col-md-6">
                <label className="form-label">
                  <i className="fa fa-calendar me-1"></i>Date Range
                </label>
                <div style={{ position: 'relative', zIndex: 101 }}>
                  <DateRangePicker
                    startDate={toValidDate(startDate)}
                    endDate={toValidDate(endDate)}
                    onChange={handleDateRangeChange}
                    placeholder="Select Date Range"
                    className="filter-dropdown"
                    allowSingleDate
                    allowDateRange
                    showTimeSelection={true}
                    popperProps={{ strategy: 'fixed', modifiers: [{ name: 'preventOverflow', options: { boundary: 'viewport' } }] }}
                  />
                </div>
              </div>
              <div className="col-xl-4 col-lg-4 col-md-6 d-flex gap-2">
                <button type="button" className="btn btn-outline-secondary" onClick={clearFilters}>
                  <i className="fa fa-times me-1"></i>Clear
                </button>
                <button type="button" className="btn btn-primary" onClick={fetchStaffWorkReport} disabled={loading}>
                  <i className={`fa ${loading ? 'fa-spinner fa-spin' : 'fa-sync-alt'} me-1`}></i>Refresh
                </button>
              </div>
            </div>
            <div className="mt-2 text-muted small">
              Showing {staffReports.length} staff member(s)
            </div>
          </div>
        </div>
      </div>

      <div className="mb-4 d-flex gap-3 flex-wrap">
        <div className="flex-grow-1" style={{ minWidth: '140px', backgroundColor: themeColor, borderRadius: '8px', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '12px', color: 'white' }}>
          <i className="fa fa-dollar-sign" style={{ fontSize: '24px', opacity: 0.9 }}></i>
          <div>
            <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{formatCurrency(summary.totalSales ?? 0)}</div>
            <div style={{ fontSize: '12px', opacity: 0.85 }}>Total Sales</div>
          </div>
        </div>
        <div className="flex-grow-1" style={{ minWidth: '140px', backgroundColor: themeColor, borderRadius: '8px', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '12px', color: 'white' }}>
          <i className="fa fa-shopping-cart" style={{ fontSize: '24px', opacity: 0.9 }}></i>
          <div>
            <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{summary.totalOrders ?? 0}</div>
            <div style={{ fontSize: '12px', opacity: 0.85 }}>Total Orders</div>
          </div>
        </div>
        <div className="flex-grow-1" style={{ minWidth: '140px', backgroundColor: themeColor, borderRadius: '8px', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '12px', color: 'white' }}>
          <i className="fa fa-money-bill-wave" style={{ fontSize: '24px', opacity: 0.9 }}></i>
          <div>
            <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{formatCurrency(summary.totalTips ?? 0)}</div>
            <div style={{ fontSize: '12px', opacity: 0.85 }}>Total Tips</div>
          </div>
        </div>
        <div className="flex-grow-1" style={{ minWidth: '140px', backgroundColor: themeColor, borderRadius: '8px', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '12px', color: 'white' }}>
          <i className="fa fa-undo" style={{ fontSize: '24px', opacity: 0.9 }}></i>
          <div>
            <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{formatCurrency(summary.totalRefunds ?? 0)}</div>
            <div style={{ fontSize: '12px', opacity: 0.85 }}>Total Refunds</div>
          </div>
        </div>
        <div className="flex-grow-1" style={{ minWidth: '140px', backgroundColor: themeColor, borderRadius: '8px', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '12px', color: 'white' }}>
          <i className="fa fa-ban" style={{ fontSize: '24px', opacity: 0.9 }}></i>
          <div>
            <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{summary.totalVoids ?? 0}</div>
            <div style={{ fontSize: '12px', opacity: 0.85 }}>Total Voids</div>
          </div>
        </div>
        <div className="flex-grow-1" style={{ minWidth: '140px', backgroundColor: themeColor, borderRadius: '8px', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '12px', color: 'white' }}>
          <i className="fa fa-percentage" style={{ fontSize: '24px', opacity: 0.9 }}></i>
          <div>
            <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{formatCurrency(summary.totalDiscounts ?? 0)}</div>
            <div style={{ fontSize: '12px', opacity: 0.85 }}>Total Discounts</div>
          </div>
        </div>
        {categorySales.map((cat) => (
          <div key={cat.categoryId} className="flex-grow-1" style={{ minWidth: '140px', backgroundColor: themeColor, borderRadius: '8px', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '12px', color: 'white' }}>
            <i className="fa fa-chart-pie" style={{ fontSize: '24px', opacity: 0.9 }}></i>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{formatCurrency(cat.saleAmount ?? 0)}</div>
              <div style={{ fontSize: '12px', opacity: 0.85 }}>{cat.categoryName || 'Category'}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="card border-0" style={{ position: 'relative', zIndex: 1 }}>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="bg-light" style={{ backgroundColor: isDarkMode ? '#2d3436' : '#f8f9fa' }}>
                <tr>
                  <th className="pt-3 pb-3">Staff Member</th>
                  <th className="pt-3 pb-3 text-center">Login Time</th>
                  <th className="pt-3 pb-3 text-end">Sale Amount</th>
                  <th className="pt-3 pb-3 text-center">Order Count</th>
                  <th className="pt-3 pb-3 text-end">Tip</th>
                  <th className="pt-3 pb-3 text-end">Refund</th>
                  <th className="pt-3 pb-3 text-center">Void Count</th>
                  <th className="pt-3 pb-3 text-end">Discounts</th>
                  {categorySales.map((c) => (
                    <th key={c.categoryId} className="pt-3 pb-3 text-end">{c.categoryName} Sale</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {staffReports.length === 0 ? (
                  <tr>
                    <td colSpan={8 + categorySales.length} className="text-center py-5">
                      <i className="fa fa-users fa-3x text-muted mb-3 d-block"></i>
                      <h5>No Staff Work Reports Found</h5>
                      <p className="text-muted">Try adjusting your filters or date range.</p>
                    </td>
                  </tr>
                ) : (
                  staffReports.map((staff) => {
                    const s = staff;
                    const firstName = s.firstName ?? s.FirstName ?? '';
                    const lastName = s.lastName ?? s.LastName ?? '';
                    const staffCategorySales = getCategorySales(s.categorySales);
                    return (
                      <tr key={s.userName ?? s.UserName ?? Math.random()}>
                        <td className="align-middle">
                          <div className="d-flex align-items-center">
                            <div className="w-40px h-40px rounded-circle bg-light d-flex align-items-center justify-content-center me-3">
                              <i className="fa fa-user text-muted"></i>
                            </div>
                            <div>
                              <div className="fw-bold">
                                {[firstName, lastName].filter(Boolean).join(' ') || s.userName || s.UserName || 'N/A'}
                              </div>
                              <div className="text-muted small">{s.roleName ?? s.RoleName ?? 'N/A'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="align-middle text-center">
                          <span className="badge bg-info">{s.loginTime ?? s.LoginTime ?? 'N/A'}</span>
                        </td>
                        <td className="align-middle text-end fw-bold">
                          {formatCurrency(s.saleAmount ?? s.SaleAmount ?? 0)}
                        </td>
                        <td className="align-middle text-center">
                          <span className="badge bg-primary">{s.orderCount ?? s.OrderCount ?? 0}</span>
                        </td>
                        <td className="align-middle text-end">{formatCurrency(s.tip ?? s.Tip ?? 0)}</td>
                        <td className="align-middle text-end">
                          <span className={(s.refund ?? s.Refund ?? 0) > 0 ? 'text-danger fw-semibold' : ''}>
                            {formatCurrency(s.refund ?? s.Refund ?? 0)}
                          </span>
                        </td>
                        <td className="align-middle text-center">
                          <span className={`badge ${(s.voidCount ?? s.VoidCount ?? 0) > 0 ? 'bg-danger' : 'bg-secondary'}`}>
                            {s.voidCount ?? s.VoidCount ?? 0}
                          </span>
                        </td>
                        <td className="align-middle text-end">
                          {formatCurrency(s.discounts ?? s.Discounts ?? 0)}
                        </td>
                        {categorySales.map((sumCat) => {
                          const sc = staffCategorySales.find((x) => (x.categoryName ?? x.CategoryName) === (sumCat.categoryName ?? sumCat.CategoryName));
                          return (
                            <td key={sumCat.categoryId} className="align-middle text-end">
                              {formatCurrency(sc?.saleAmount ?? sc?.SaleAmount ?? 0)}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffWorkReport;
